// O-4756. The active member's settlement columns and the rule for putting them
// back after the server refuses a save. Both editors write these columns, so
// both need the same rule; keeping it here rather than in one editor's autosave
// hook is what stops the two from drifting apart.
//
// They move as a PAIR everywhere they appear — echoed together, snapshotted
// together, restored together — because the share is read against a different
// base per strategy. Restoring one without the other reinterprets it: a 100 kept
// from `percentage_of_remainder` is paid against the WHOLE pool the moment the
// strategy reverts to `explicit`.
export const POOL_SHARE_FIELDS = ['pool_percentage', 'pool_payout_strategy'];

// The rest of the member's deal that the pool card writes. These are restored
// PER FIELD rather than as a pair — they are independent of one another, unlike
// the share and its strategy — but they are just as refusable (a cap must be
// above 0 and above its floor), and a refused value left in the shadow rides on
// every later PATCH, so the whole pane silently stops saving.
export const POOL_MEMBER_FIELDS = [
  ...POOL_SHARE_FIELDS, 'dollar_amount', 'dollar_or_percentage_operator',
  'artist_payout_cap', 'artist_payout_floor',
];

const norm = (source, field) => source?.[field] ?? null;

// Value equality across the layer boundary. `pool_percentage` is a Rails
// `decimal`, which serialises as a STRING — a save that confirms 100 answers
// with "100.0" — while the editors hold it as a Number. A raw `===` between the
// two is never true, so a guard written with one silently becomes "always" or
// "never" depending on which side it sits on. Compare numerically when both
// sides are numeric, strictly otherwise (the strategy is a plain string).
const same = (a, b) => {
  if (a === b) return true;
  if (a === null || b === null) return false;
  const na = Number(a);
  const nb = Number(b);
  if (Number.isNaN(na) || Number.isNaN(nb)) return false;
  return na === nb;
};

// Which fields to put back after a refused save, or null for "leave it alone".
//
// `previous` is the pre-edit snapshot, `sent` the payload the server actually
// rejected, `current` what the editor holds now.
//
// All-or-nothing, and that is the whole point. A per-field filter — restore
// each column the user has not since retyped — reads as the careful choice and
// is the more dangerous one: a booker who clears the seeded 100 to type a real
// 60 while the rejection is in flight keeps their 60 (retyped, newer intent)
// AND has the strategy reverted underneath it, so a share authored against the
// remainder is silently re-based onto the whole pool. That pair is valid, so
// the server accepts it and nothing warns. If either column has moved on, the
// rejection is stale for both.
// The independent fields: restored per field, all-or-nothing does not apply.
export const restorableMemberFields = ({ previous, sent, current }) => {
  if (!previous || !sent) return null;

  const restore = {};
  POOL_MEMBER_FIELDS.filter((f) => !POOL_SHARE_FIELDS.includes(f))
    .filter((f) => f in sent)
    .forEach((f) => {
      // Only what the booker has not since retyped.
      if (!same(norm(current, f), norm(sent, f))) return;
      if (!same(norm(current, f), norm(previous, f))) restore[f] = norm(previous, f);
    });
  return Object.keys(restore).length > 0 ? restore : null;
};

export const restorablePoolShare = ({ previous, sent, current }) => {
  if (!previous || !sent) return null;

  // Only the columns this request actually carried. The complex editor PATCHes
  // the whole offer so both are always present; the simple editor sends just
  // the field that changed, and a column the server was never told about was
  // not refused and has nothing to roll back.
  const fields = POOL_SHARE_FIELDS.filter((f) => f in sent);
  if (fields.length === 0) return null;

  const retyped = fields.some((f) => !same(norm(current, f), norm(sent, f)));
  if (retyped) return null;

  const restore = {};
  fields.forEach((f) => {
    if (!same(norm(current, f), norm(previous, f))) restore[f] = norm(previous, f);
  });
  return Object.keys(restore).length > 0 ? restore : null;
};

// A snapshot covers one edit window, so it must survive a save that confirms
// only part of it. Clearing on any 200 loses the snapshot for keystrokes that
// landed WHILE that request was in flight, and their rejection then has nothing
// to restore — the refused value stays in the shadow, rides on every later
// PATCH, and the offer silently stops persisting anything.
export const poolShareConfirmed = ({ current, confirmed }) => (
  POOL_SHARE_FIELDS.filter((f) => f in (confirmed || {}))
    .every((f) => same(norm(current, f), norm(confirmed, f)))
);
